import { useId, useState } from 'react';
import { PERMISSIONS } from '@inference.engine/permissions/web';
import { useQuery } from '@tanstack/react-query';
import {
  Checkbox,
  Input,
  InputDecoration,
  InputGroup,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  Separator,
} from '@monksflow/monks-ui';
import { Building2Icon, SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  trackerScopeQueryOptions,
  useUpdateTrackerScopeMutation,
  type TrackerBoardType,
  type TrackerScope,
  type UpdateTrackerScope,
} from '@/entities/tracker';
import { useAuth, useCurrentOrganizationWorkspaces } from '@/entities/user';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';

import { useWorkspaceScope } from '../../lib/useWorkspaceScope';

type ScopeMode = TrackerScope['mode'];

const MODES: readonly ScopeMode[] = ['board', 'selected', 'all'];

/** Above this many workspaces the checkbox list gets a search box. */
const SEARCH_ABOVE = 8;

function isScopeMode(value: string): value is ScopeMode {
  return (MODES as readonly string[]).includes(value);
}

/**
 * Which workspaces a record board reads its rows from. Every change is saved
 * as it is made, and the board's own workspace is always part of the scope.
 */
export function WorkspaceScopeChip({
  trackerId,
  boardType,
}: {
  trackerId: string;
  boardType: TrackerBoardType;
}) {
  const { t } = useTranslation('trackers');
  const { workspaceId, scope } = useWorkspaceScope(trackerId);
  const update = useUpdateTrackerScopeMutation({ workspaceId, trackerId });
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const baseId = useId();
  const memberships = useCurrentOrganizationWorkspaces();
  const isOrganizationAdmin = useAuth((state) =>
    (state.signedInUser.value?.permissions ?? []).some(
      (permission) =>
        permission === PERMISSIONS.OrganizationAdmin || permission === PERMISSIONS.InstanceAdmin,
    ),
  );
  // A board scope comes without the workspaces to pick from, since each costs
  // the server a platform check. They are fetched once the picker opens.
  const listQuery = useQuery({
    ...trackerScopeQueryOptions(workspaceId, trackerId, { withAvailable: true }),
    enabled: open && Boolean(scope) && !scope?.available,
  });

  if (!scope) return null;
  const listed = scope.available ?? listQuery.data?.available;
  const mayWiden = listed ? listed.length > 1 : memberships.length > 1 || isOrganizationAdmin;
  // Kept while open, so a list that comes back with the board alone explains
  // itself instead of the popover vanishing under the click.
  if (!mayWiden && !open) return null;
  const listUnavailable = !listed && listQuery.isError;
  const nothingToAdd = listed?.length === 1;
  const widenDisabled = listUnavailable || nothingToAdd;

  const boardWorkspace = listed?.find((workspace) => workspace.id === workspaceId) ??
    listed?.[0] ?? {
      id: workspaceId,
      name: memberships.find((workspace) => workspace.id === workspaceId)?.name ?? null,
    };
  const available = listed ?? [boardWorkspace];
  const nameOf = (workspace: NonNullable<TrackerScope['available']>[number]) =>
    workspace.name ?? t('trackersBoard.workspaceScope.unnamedWorkspace');

  // While a change is in flight the controls show what was asked for, so the
  // list does not flick back to the old answer before the new one lands.
  const pending = update.isPending ? update.variables : undefined;
  const mode = pending?.mode ?? scope.mode;
  const selectedIds = new Set([
    boardWorkspace.id,
    ...(pending ? (pending.workspaceIds ?? []) : scope.workspaceIds),
  ]);

  const change = (body: UpdateTrackerScope) => {
    update.mutate(body, {
      onSuccess: (next) =>
        track('tracker_scope_changed', {
          tracker_id: trackerId,
          board_type: boardType,
          mode: next.mode,
          workspace_count: next.resolvedWorkspaceIds.length,
        }),
      onError: () => notificationService.showError(t('trackersBoard.workspaceScope.notSaved')),
    });
  };

  const changeMode = (value: string) => {
    if (!isScopeMode(value)) return;
    if (value !== 'selected') {
      change({ mode: value });
      return;
    }
    change({
      mode: 'selected',
      workspaceIds: [...new Set([boardWorkspace.id, ...scope.workspaceIds])],
    });
  };

  const toggleWorkspace = (id: string, checked: boolean) => {
    const listedIds = new Set(available.map((workspace) => workspace.id));
    // A selected workspace missing from the list has no checkbox to untick, so
    // a change made here must not drop it.
    const unlisted = scope.workspaceIds.filter((candidate) => !listedIds.has(candidate));
    const workspaceIds = [...listedIds].filter((candidate) =>
      candidate === id ? checked : candidate === boardWorkspace.id || selectedIds.has(candidate),
    );
    change({ mode: 'selected', workspaceIds: [...workspaceIds, ...unlisted] });
  };

  const resolvedCount = scope.resolvedWorkspaceIds.length;
  const value =
    scope.mode === 'all'
      ? t('trackersBoard.workspaceScope.valueAll', { total: resolvedCount })
      : scope.mode === 'selected'
        ? t('trackersBoard.workspaceScope.valueSelected', { count: resolvedCount })
        : t('trackersBoard.workspaceScope.valueBoard');

  const modeLabel: Record<ScopeMode, string> = {
    board: t('trackersBoard.workspaceScope.board'),
    selected: t('trackersBoard.workspaceScope.selected'),
    all: t('trackersBoard.workspaceScope.all'),
  };

  const searchable = available.length > SEARCH_ABOVE;
  const query = searchable ? search.trim().toLowerCase() : '';
  const visible = query
    ? available.filter((workspace) => nameOf(workspace).toLowerCase().includes(query))
    : available;

  const rowsFromId = `${baseId}-rows-from`;
  const boardNameId = `${baseId}-board-name`;
  const listId = `${baseId}-list`;
  const thisBoardId = `${baseId}-this-board`;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('trackersBoard.workspaceScope.trigger', { value })}
          className={[
            'inline-flex h-9 max-w-65 min-w-0 cursor-pointer items-center gap-1.5 rounded-md border border-border pl-3 pr-2 text-sm text-foreground transition-colors hover:bg-accent',
            'outline-none focus-visible:ring-2 focus-visible:ring-ring',
            scope.mode === 'board' ? 'bg-card' : 'bg-accent',
          ].join(' ')}
        >
          <Building2Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="shrink-0 text-muted-foreground">
            {t('trackersBoard.workspaceScope.triggerPrefix')}
          </span>
          <span className="min-w-0 truncate font-medium">{value}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" aria-labelledby={rowsFromId} className="flex w-65 flex-col p-0">
        <div className="flex flex-col gap-2 p-3">
          <p id={rowsFromId} className="text-xs font-medium text-muted-foreground">
            {t('trackersBoard.workspaceScope.rowsFrom')}
          </p>
          <RadioGroup
            aria-labelledby={rowsFromId}
            value={mode}
            onValueChange={changeMode}
            disabled={update.isPending}
            className="gap-2"
          >
            {MODES.map((option) => (
              <div key={option} className="flex min-w-0 items-center gap-2">
                <RadioGroupItem
                  id={`${baseId}-${option}`}
                  value={option}
                  disabled={option !== 'board' && option !== mode && widenDisabled}
                  aria-describedby={option === 'board' ? boardNameId : undefined}
                />
                <Label htmlFor={`${baseId}-${option}`} className="shrink-0 font-normal">
                  {modeLabel[option]}
                </Label>
                {option === 'board' ? (
                  <span id={boardNameId} className="min-w-0 truncate text-xs text-muted-foreground">
                    {nameOf(boardWorkspace)}
                  </span>
                ) : null}
              </div>
            ))}
          </RadioGroup>
          {listUnavailable || nothingToAdd ? (
            <p role="status" className="text-xs text-muted-foreground">
              {listUnavailable
                ? t('trackersBoard.workspaceScope.listFailed')
                : t('trackersBoard.workspaceScope.noOtherWorkspaces')}
            </p>
          ) : null}
        </div>

        {mode === 'selected' ? (
          <>
            <Separator />
            <div role="group" aria-labelledby={listId} className="flex flex-col gap-2 p-3">
              <p id={listId} className="text-xs font-medium text-muted-foreground">
                {t('trackersBoard.workspaceScope.list')}
              </p>
              {searchable ? (
                <InputGroup size="sm">
                  <InputDecoration muted>
                    <SearchIcon className="size-3.5" />
                  </InputDecoration>
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t('trackersBoard.workspaceScope.searchPlaceholder')}
                    aria-label={t('trackersBoard.workspaceScope.search')}
                  />
                </InputGroup>
              ) : null}
              <div className="flex max-h-60 flex-col gap-2 overflow-y-auto">
                {visible.map((workspace) => {
                  const isBoard = workspace.id === boardWorkspace.id;
                  const checkboxId = `${baseId}-ws-${workspace.id}`;
                  return (
                    <div key={workspace.id} className="flex min-w-0 items-center gap-2">
                      <Checkbox
                        id={checkboxId}
                        checked={selectedIds.has(workspace.id)}
                        disabled={isBoard || update.isPending}
                        onCheckedChange={(checked) =>
                          toggleWorkspace(workspace.id, checked === true)
                        }
                        aria-describedby={isBoard ? thisBoardId : undefined}
                      />
                      <Label htmlFor={checkboxId} className="min-w-0 truncate font-normal">
                        {nameOf(workspace)}
                      </Label>
                      {isBoard ? (
                        <span id={thisBoardId} className="shrink-0 text-xs text-muted-foreground">
                          {t('trackersBoard.workspaceScope.thisBoard')}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
                {visible.length === 0 ? (
                  <p className="py-2 text-center text-sm text-muted-foreground">
                    {t('trackersBoard.workspaceScope.noMatches')}
                  </p>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
